/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-09-11: Create Slide Deck dialog for Frame-as-slides
 * Phase 4: Mechanism 4 enhancement - rapid deck structure creation
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';

import { useAppStore, useLinkStore } from '@sage3/frontend';

type CreateSlideDeckDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  boardId: string;
  startPosition?: { x: number; y: number };
};

/**
 * Dialog for creating a deck of Frames (slides) in grid layout
 *
 * Features:
 * - Configurable number of slides (default: 10)
 * - Configurable grid columns (default: 5)
 * - Auto-titled "Slide 1", "Slide 2", etc.
 * - Auto-linked in narrative sequence
 * - Standard presentation size (1920×1080)
 */
export function CreateSlideDeckDialog({
  isOpen,
  onClose,
  roomId,
  boardId,
  startPosition = { x: 0, y: 0 },
}: CreateSlideDeckDialogProps) {
  const [slideCount, setSlideCount] = useState(10);
  const [columns, setColumns] = useState(5);
  const [isCreating, setIsCreating] = useState(false);

  const createApp = useAppStore((state) => state.create);
  const addLink = useLinkStore((state) => state.addLink);
  const toast = useToast();

  const handleCreate = async () => {
    setIsCreating(true);

    try {
      const slideSize = { width: 1920, height: 1080, depth: 0 };
      const spacing = 200;

      const frameIds: string[] = [];

      // Create all Frames in grid layout
      for (let i = 0; i < slideCount; i++) {
        const row = Math.floor(i / columns);
        const col = i % columns;

        const position = {
          x: startPosition.x + col * (slideSize.width + spacing),
          y: startPosition.y + row * (slideSize.height + spacing),
          z: 0,
        };

        const result = await createApp({
          title: `Slide ${i + 1}`,
          roomId,
          boardId,
          position,
          size: slideSize,
          rotation: { x: 0, y: 0, z: 0 },
          type: 'Frame' as const,
          state: { executeInfo: { executeFunc: '', params: {} } },
          raised: false,
          dragging: false,
          pinned: false,
        });

        if (result.success && result.data?._id) {
          frameIds.push(result.data._id);
        }
      }

      // Auto-link Frames in sequence
      for (let i = 0; i < frameIds.length - 1; i++) {
        await addLink(frameIds[i], frameIds[i + 1], boardId, 'narrative_order');
      }

      toast({
        title: 'Slide Deck Created',
        description: `Created ${slideCount} slides in ${Math.ceil(slideCount / columns)}×${Math.min(slideCount, columns)} grid`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: 'Could not create slide deck. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Slide Deck</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Number of Slides</FormLabel>
              <NumberInput
                value={slideCount}
                onChange={(_, val) => setSlideCount(val)}
                min={1}
                max={50}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm" color="gray.500" mt={1}>
                How many Frames to create (1-50)
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Grid Columns</FormLabel>
              <NumberInput
                value={columns}
                onChange={(_, val) => setColumns(val)}
                min={1}
                max={10}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Frames per row (1-10)
              </Text>
            </FormControl>

            <Text fontSize="sm" color="blue.600" fontWeight="medium">
              Grid: {Math.ceil(slideCount / columns)} rows × {Math.min(slideCount, columns)} columns
            </Text>

            <Text fontSize="xs" color="gray.600">
              • Each Frame: 1920×1080 (standard presentation size)
              <br />
              • Spacing: 200 pixels
              <br />
              • Auto-linked in reading order (left to right, top to bottom)
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isCreating}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreate}
            isLoading={isCreating}
            loadingText="Creating..."
          >
            Create Deck
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
